import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEffect, useRef } from 'react';

const TILE_SIZE_IN_PIXELS = 250;

interface MapDebugProps {
  map: google.maps.Map;
  googleMaps: typeof google.maps;
}

export const MapDebug = ({ map, googleMaps }: MapDebugProps) => {
  const mapRectangleRef = useRef<google.maps.Rectangle | null>(null);
  const centerMarkerRef = useRef<google.maps.Marker | null>(null);

  const initializeOverlays = useEventCallback((projection: google.maps.Projection) => {
    if (!centerMarkerRef.current) {
      centerMarkerRef.current = new googleMaps.Marker({
        position: map.getCenter(),
        map,
        icon: {
          path: googleMaps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#ff0000',
          fillOpacity: 1,
          strokeWeight: 5,
          strokeColor: '#ffffff',
        },
        zIndex: 1000,
      });
    }

    const updateOverlays = () => {
      const bounds = map.getBounds();
      if (!bounds) return;

      centerMarkerRef.current?.setPosition(map.getCenter());

      const paddingPx = 10;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const topLeft = projection.fromLatLngToPoint(new googleMaps.LatLng(ne.lat(), sw.lng()));
      const bottomRight = projection.fromLatLngToPoint(new googleMaps.LatLng(sw.lat(), ne.lng()));

      if (!bottomRight || !topLeft) return;

      const scale = Math.pow(2, map.getZoom() ?? 0);

      const pxToCoord = (px: number) => px / TILE_SIZE_IN_PIXELS / scale;

      const newNE = projection.fromPointToLatLng(new googleMaps.Point(bottomRight.x - pxToCoord(paddingPx), topLeft.y + pxToCoord(paddingPx)));
      const newSW = projection.fromPointToLatLng(new googleMaps.Point(topLeft.x + pxToCoord(paddingPx), bottomRight.y - pxToCoord(paddingPx)));

      const reducedBounds = new googleMaps.LatLngBounds(newSW, newNE);

      if (mapRectangleRef.current) {
        mapRectangleRef.current.setBounds(reducedBounds);
      } else {
        mapRectangleRef.current = new googleMaps.Rectangle({
          bounds: reducedBounds,
          strokeColor: '#ff0000',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillOpacity: 0.1,
          map,
        });
      }
    };

    updateOverlays();

    const idleListener = map.addListener('idle', updateOverlays);

    return () => {
      googleMaps.event.removeListener(idleListener);
    };
  });

  useEffect(() => {
    const projection = map.getProjection();

    if (!projection) {
      const listener = map.addListener('projection_changed', () => {
        const delayedProjection = map.getProjection();
        if (delayedProjection) {
          initializeOverlays(delayedProjection);
          googleMaps.event.removeListener(listener);
        }
      });

      return () => {
        googleMaps.event.removeListener(listener);
      };
    }

    initializeOverlays(projection);

    return () => {
      centerMarkerRef.current?.setMap(null);
      centerMarkerRef.current = null;

      mapRectangleRef.current?.setMap(null);
      mapRectangleRef.current = null;
    };
  }, [map, googleMaps, initializeOverlays]);

  return null;
};
